import React from "react";
import Modal from "../../common/Modal/Modal";
import ModalContent from "../../common/Modal/ModalContent/ModalContent";
import ModalBody from "../../common/Modal/ModalBody/ModalBody";
import ModalFooter from "../../common/Modal/ModalFooter/ModalFooter";
import ModalHeader from "../../common/Modal/ModalHeader/ModalHeader";
import { IEditPostModalProps } from "./EditPostModal.types";
import { styles } from "./EditPostModal.styles";
import { useIsDesktop } from "../../../utils/common/screen";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PostTypeSelector from "../PostTypeSelector/PostTypeSelector";
import LazyLoadedPostVideo from "../PostVideo/LazyLoadedPostVideo";
import LazyLoadedPostOffering from "../PostOffering/LazyLoadedPostOffering";
import TextAreaWithAutoHeight from "../../common/FormInputs/TextArea/TextAreaWithAutoHeight";
import { DEFAULT_USER_PROFILE_IMG_SRC } from "../../../assets/user";
import { LazyLoadedPostImagesPicker } from "./modules/PostImagesPicker/LazyLoadedPostImagesPicker";
import LazyLoadedPostDocViewer from "./modules/PostDocPicker/LazyLoadedPostDocPicker";
import { LazyLoadedPostAudio } from "../PostAudio/LazyLoadedPostAudio";
import { classNames } from "../../../utils/common/styling";

const EditPostModal = ({
  // generic modal component props
  isOpen,
  onClose,
  VideoPlayerComponent,

  // other EditPostModalProps
  title = "Edit post",
  authorImgSrc = DEFAULT_USER_PROFILE_IMG_SRC,
  authorName,
  postContentDefaultValue,
  postContentTextAreaRef,
  SaveButton = () => null,
  Loader,
  makeModalInvisible,
  CustomTextEditorComponent,

  // post type generic props
  postTypeSelectorProps,
  postMediaUploading,
  postMediaIsAdded,

  // post type media props
  postImageProps,
  postDocProps,
  postVideoProps,
  postAudioProps,
  postOfferingProps,

  keepMounted,

  // patch fix for RTE
  // bug: RTE is not working properly on iOS inside MUI v5 Modal: unable to add space in editor
  // solution: inject Modal component from parent component. injecting MUI v4 modal from creator tool
  customModalComponent,
  postDuplicationNode,
  AutoExpandPostComponent,
  autoExpandPostComponentProps,
  PaidPostComponent,
  paidPostComponentProps,
  classes,
}: IEditPostModalProps) => {
  const isDesktop = useIsDesktop();

  const PostTypeSelectorComponent = (
    <div
      className={classNames(
        styles.postTypeSelectorContainer,
        classes?.postTypeSelector,
      )}
    >
      <PostTypeSelector {...postTypeSelectorProps} />
    </div>
  );

  const ModalComponent = customModalComponent || Modal;
  const TextEditorComponent =
    CustomTextEditorComponent || TextAreaWithAutoHeight;

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      classes={{ root: makeModalInvisible ? styles.invisible : "" }}
      keepMounted={keepMounted}
    >
      <ModalContent
        className={classNames(styles.contentRoot, classes?.content)}
      >
        {isDesktop ? (
          <ModalHeader onClose={onClose} hideCloseIcon={!isDesktop}>
            {title}
          </ModalHeader>
        ) : (
          <div className={styles.headerRoot}>
            <div className={styles.titleContainer}>
              <CloseRoundedIcon className={styles.close} onClick={onClose} />
              {title}
            </div>
            <SaveButton />
          </div>
        )}
        <ModalBody className={styles.body}>
          <div className={styles.postCreatorDetailsContainer}>
            <img
              src={authorImgSrc}
              width={46}
              height={46}
              className={styles.authorImg}
              alt={authorName}
            />
            <span className={styles.authorName}>{authorName}</span>
          </div>

          {postDuplicationNode}

          <TextEditorComponent
            autoFocus
            placeholder="What would you like to talk about?"
            inputRef={postContentTextAreaRef}
            defaultValue={postContentDefaultValue}
            rows={1}
          />

          {AutoExpandPostComponent && (
            <AutoExpandPostComponent {...autoExpandPostComponentProps} />
          )}

          {PaidPostComponent && (
            <PaidPostComponent {...paidPostComponentProps} />
          )}

          {postMediaUploading ? <Loader /> : null}
          {postMediaIsAdded ? (
            <>
              <div className={styles.mediaSeparator} />
              <LazyLoadedPostImagesPicker {...postImageProps} />
              <LazyLoadedPostDocViewer {...postDocProps} />
              <LazyLoadedPostVideo
                {...postVideoProps}
                VideoPlayerComponent={VideoPlayerComponent}
              />
              {postAudioProps && (
                <LazyLoadedPostAudio {...postAudioProps} isRemovable />
              )}
              <LazyLoadedPostOffering
                {...postOfferingProps}
                classes={{
                  container: styles.offeringContainer,
                  image: styles.offeringImage,
                }}
              />
            </>
          ) : null}
        </ModalBody>
        {isDesktop ? (
          <ModalFooter className={styles.footer}>
            {PostTypeSelectorComponent}
            <SaveButton />
          </ModalFooter>
        ) : (
          PostTypeSelectorComponent
        )}
      </ModalContent>
    </ModalComponent>
  );
};

export default EditPostModal;
